import { ReactElement, useEffect, useRef, useState } from 'react';

// Components
import { SMPFlex, SMPText, SMPImage, Dropdown, PillTabs, SMPLinkWrapper } from '../../../index';
import { ISubscriberInfo } from '../../../../types';
import { STANDINGS_DATA_ELEMENTS_BREAKPOINT } from '../../../../constants/generic';
import { setNationalParticipantBorder } from '@sportal365/smp-widget-utils';

type HeaderOptions = {
  label: string;
  value: string;
};

type HeaderTabOptions = {
  name: string;
  value: string;
};

type StandingsHeaderProps = {
  logo?: string | null;
  link?: string;
  title?: string;
  urlTarget?: string;
  selectedTab?: string;
  selectedOption?: string;
  secondaryTitle?: string;
  options?: HeaderOptions[];
  secondaryHeader?: ReactElement;
  tabOptions?: HeaderTabOptions[];
  isMainImageRound?: boolean;
  handleClick?: () => void;
  handleTabOptionClick?: (optionId: string) => void;
  handleChange?: (value?: string) => void;
  handleSubscriptionClick: (info: ISubscriberInfo, clickType: string) => void;
  displayMainHeader: boolean;
  dataOptionsDisplay?: boolean;
  mobileDropdown?: boolean;
};

export const Header = ({
  logo,
  link,
  title,
  urlTarget,
  options,
  tabOptions,
  selectedOption,
  secondaryTitle,
  selectedTab,
  handleClick,
  handleChange,
  secondaryHeader,
  handleTabOptionClick,
  handleSubscriptionClick,
  isMainImageRound,
  displayMainHeader,
  dataOptionsDisplay,
  mobileDropdown,
}: StandingsHeaderProps) => {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const displayTabOptions = !!(dataOptionsDisplay && tabOptions && tabOptions.length > 1);

  const [isMobile, setIsMobile] = useState(false);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        if (newWidth !== width && headerRef.current) {
          setWidth(newWidth);
          setIsMobile(newWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT);

          headerRef.current.style.setProperty('--filters-width', `${newWidth}px`);
        }
      }
    });

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [width, displayTabOptions]);

  return (
    <>
      {displayMainHeader && (
        <SMPFlex
          ref={headerRef}
          variant='header'
          wrap='wrap'
          gap='lg'
          css={{
            pb: displayTabOptions || secondaryHeader !== undefined ? undefined : '$2_5',
            bc: '$rowBackgroundColor',
            flexDirection: 'column',
            '@media (min-width: 640px)': {
              flexDirection: 'row',
              alignItems: 'center',
            },
          }}
        >
          <SMPLinkWrapper href={link} target={urlTarget} onClick={handleClick}>
            <SMPFlex grow={1} align={'center'} css={{ justifyContent: 'start' }}>
              {logo && (
                <SMPImage
                  variant='livescoreEntityLogo'
                  src={logo}
                  alt={`${title} Logo`}
                  css={{ mr: '$1', br: isMainImageRound ? '$round' : '', ...setNationalParticipantBorder(!!isMainImageRound) }}
                />
              )}

              <SMPFlex variant='column'>
                <SMPText variant='contrast' size='3' weight='700'>
                  {title}
                </SMPText>
                <SMPText variant='contrast' size='2'>
                  {secondaryTitle}
                </SMPText>
              </SMPFlex>
            </SMPFlex>
          </SMPLinkWrapper>
          {options && (
            <Dropdown
              options={options}
              value={selectedOption}
              onChange={handleChange}
              handleSubscriptionClick={handleSubscriptionClick}
              parentRef={headerRef}
            />
          )}
        </SMPFlex>
      )}
      {displayTabOptions && (
        <SMPFlex variant='standingsFilters' ref={headerRef}>
          <PillTabs
            tabs={tabOptions}
            selectedTab={selectedTab}
            handleTabClick={handleTabOptionClick}
            handleSubscriptionClick={handleSubscriptionClick}
            mobileDropdown={mobileDropdown && isMobile}
            parentRef={headerRef}
          />
        </SMPFlex>
      )}

      {secondaryHeader && <SMPFlex variant='standingsFilters'>{secondaryHeader}</SMPFlex>}
    </>
  );
};
